import { Sheet } from '@/components/ui/Sheet'
import { brl, dateBR, roundToStep } from '@/lib/format'
import type { CommissionDetail } from '@/services/modules/RevenuesService'

const STATUS_DOT: Record<string, { color: string; label: string }> = {
  PENDENTE:    { color: 'bg-amber-400',   label: 'Pendente' },
  PAGO:        { color: 'bg-emerald-500', label: 'Pago' },
  ISENTO:      { color: 'bg-blue-500',    label: 'Isento' },
  CANCELADO:   { color: 'bg-rose-500',    label: 'Cancelado' },
  RECUSADO:    { color: 'bg-rose-500',    label: 'Recusado' },
  REEMBOLSADO: { color: 'bg-blue-500',    label: 'Reembolsado' },
  PROCESSADO:  { color: 'bg-blue-500',    label: 'Processado' },
}

const ROLE_LABEL: Record<string, string> = {
  DIRETOR: 'Diretor', SUPERVISOR: 'Supervisor', AFILIADO: 'Afiliado', VENDEDOR: 'Vendedor',
}

interface Props {
  open: boolean
  onClose: () => void
  title: string
  subtitle?: string
  loading?: boolean
  detail: CommissionDetail | null
  /** Mostra o recebedor da comissão (visões "a pagar" / admin). */
  showRecipient?: boolean
}

/**
 * Drawer de detalhamento de comissões: lista, fatura a fatura, o cliente, o
 * valor da fatura, a base sobre a qual a comissão incidiu, o percentual efetivo
 * e o valor em reais. Reutilizado por Ganhos (parceiro) e pelas duas telas de
 * Comissionamento (parceiro + admin).
 */
export function CommissionDetailDrawer({ open, onClose, title, subtitle, loading, detail, showRecipient }: Props) {
  const items = detail?.items ?? []
  // Apenas o PERCENTUAL é arredondado para a escala de 0,10 (ex.: 8,02% / 7,99% /
  // 8,01% → 8% · 14,99% → 15%). Os valores em R$ (comissão e total) ficam intactos.
  return (
    <Sheet open={open} onClose={onClose} title={title} subtitle={subtitle} size="xl">
      {/* Total do recorte */}
      <div className="mb-4 flex items-center justify-between rounded-md bg-gray-50 px-4 py-3 ring-1 ring-gray-200">
        <span className="text-sm text-gray-600">Total ({items.length} {items.length === 1 ? 'fatura' : 'faturas'})</span>
        <span className="text-lg font-semibold text-gray-900">{brl(detail?.total ?? 0)}</span>
      </div>

      {loading ? (
        <p className="py-8 text-center text-sm text-gray-400">Carregando…</p>
      ) : items.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-400">Nenhuma comissão neste recorte.</p>
      ) : (
        <ul className="space-y-2.5">
          {items.map((it, i) => {
            const dot = STATUS_DOT[it.status] ?? { color: 'bg-gray-300', label: it.status }
            const dateLabel = it.paidDate
              ? `pago em ${dateBR(it.paidDate)}`
              : it.dueDate ? `vence em ${dateBR(it.dueDate)}` : '—'
            return (
              <li key={`${it.revenueId}-${i}`} className="rounded-md ring-1 ring-gray-200 px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${dot.color}`} title={dot.label} />
                      <span className="break-words font-medium text-gray-800">{it.client}</span>
                    </div>
                    <div className="mt-0.5 text-[11px] text-gray-400">
                      Fatura #{it.code} · {dateLabel}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="font-semibold text-gray-900">{brl(it.amount)}</div>
                    <div className="text-[11px] text-gray-400">comissão</div>
                  </div>
                </div>

                {/* Memória de cálculo: base × % = comissão */}
                <div className="mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[12px] text-gray-600">
                  <span className="rounded bg-gray-100 px-1.5 py-0.5">Base {brl(it.base)}</span>
                  <span className="text-gray-400">×</span>
                  <span className="rounded bg-brand-50 px-1.5 py-0.5 font-medium text-brand-700">{roundToStep(it.percentage).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%</span>
                  <span className="text-gray-400">=</span>
                  <span className="font-medium text-gray-800">{brl(it.amount)}</span>
                  {it.base !== it.invoiceAmount && (
                    <span className="text-[11px] text-gray-400">(fatura {brl(it.invoiceAmount)})</span>
                  )}
                </div>

                {showRecipient && it.recipientName && (
                  <div className="mt-1.5 text-[11px] text-gray-500">
                    Recebedor: <span className="text-gray-700">{it.recipientName}</span>
                    {it.recipientType && <span className="text-gray-400"> · {ROLE_LABEL[it.recipientType] ?? it.recipientType}</span>}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </Sheet>
  )
}

export default CommissionDetailDrawer
